import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { nameDataset } from '@/entities/dataset';
import { isAdmin, useIdentity } from '@/entities/session';
import { ProviderMark, jobTone, useIncidentTicket } from '@/features/itsm';
import { admitsKind, admitsSeverity, useRoutingPolicy } from '@/features/alert-routing';
import { DatasetKindIcon, useDatasetNames } from '@/features/catalog-browser';
import type {
  Incident,
  NotificationRule,
  Severity,
} from '@/shared/api/generated/observability';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import {
  useAcknowledgeIncident,
  useIncident,
  useIncidentNotifications,
  useResolveIncident,
} from '../api/incidents';
import {
  ageReading,
  blockedReasonKey,
  canAcknowledge,
  canResolve,
  deliveryReading,
  deliveryTone,
  isDefaultNotifiedSeverity,
  shortIncidentId,
  stateLabelKey,
  stateMeaningKey,
  subjectKindOf,
  timelineFor,
} from '../model/incidents';

const SEVERITY_COLOUR: Readonly<Record<Severity, string>> = {
  critical: 'var(--status-error)',
  warning: 'var(--status-warn)',
  info: 'var(--status-info)',
};

/** An instant a person reads, in their own locale. A raw ISO string makes every reader do timezone
 * arithmetic on the one panel a review reads. */
function instant(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
}

/* One row of the incident's own record.
 *
 * `mono` is for a name a reader will compare against something else — a schema-qualified table, a
 * connection, an id — and prose everywhere else. The reference sets exactly those three in the
 * monospace face for the same reason: they are strings to be matched, not read.
 */
function Fact({
  label,
  mono = false,
  children,
}: {
  readonly label: string;
  readonly mono?: boolean;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div className="lq-fact">
      <div className="lq-fact__key">{label}</div>
      <div className={mono ? 'lq-fact__val lq-mono' : 'lq-fact__val'}>{children}</div>
    </div>
  );
}

/** A panel with a ruled, uppercase heading — the reference's three-panel geometry. */
function Panel({
  heading,
  note,
  children,
}: {
  readonly heading: string;
  readonly note: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <section className="lq-ipanel">
      <h2 className="lq-ipanel__head">{heading}</h2>
      {children}
      {/* The note under each panel is not decoration: each one states the thing the panel's own
          contents cannot say for themselves — why a subject may have no name, why an empty routing
          list is still a fact, why an acknowledgement has no time. */}
      <p className="lq-ipanel__note">{note}</p>
    </section>
  );
}

/* **Who would be told, derived from the tenant's own routing rules — never a claim that anything was
 * sent.**
 *
 * The reference shows a tick and a delivery time per destination. **Those now live in the
 * `Delivery` panel below, read from the delivery log itself**, and this panel deliberately did not
 * grow them: when it was written the attempts were exposed through no route, so a tick here would
 * have been an assertion nothing behind this screen could support. The fix was to serve the
 * attempts rather than to soften the tick.
 *
 * Keeping the two apart is the point. A rule that *matches* and a delivery that *happened* are
 * different facts — a matching rule whose transport is unconfigured matches and delivers nothing —
 * and one panel showing both would invite a reader to take the first for the second. The mark below
 * still means "matches", and the copy beside it still says so.
 *
 * So the panel answers the question it *can*: which of this tenant's rules match this incident's
 * kind and severity. `admitsKind` and `admitsSeverity` are the alert-routing feature's own
 * predicates, reused rather than re-derived — a second answer to "does this rule cover this
 * incident" is how one screen reassures somebody the other is warning.
 */
function Routing({ incident }: { readonly incident: Incident }): ReactElement {
  const { t } = useTranslation();
  const policy = useRoutingPolicy();

  if (policy.isPending) return <SkeletonRows rows={2} columns={2} />;
  if (policy.isError) {
    /* A failed read is not "nobody was told". Reported as a failure of the request, because the
     * alternative is a statement about this tenant's routing that nothing measured. */
    return <Alert tone="error">{t('incidents.routing.unreadable')}</Alert>;
  }

  const matching = policy.data.rules.filter(
    (rule: NotificationRule) =>
      admitsSeverity(rule, incident.severity) && admitsKind(rule, incident.kind),
  );

  if (matching.length === 0) {
    return (
      <p className="lq-iroute__none">
        {t(
          policy.data.declared
            ? 'incidents.routing.noneDeclared'
            : 'incidents.routing.noneDefault',
        )}
      </p>
    );
  }

  return (
    <>
      {matching.map((rule) => (
        <div key={rule.id} className="lq-iroute">
          {/* The mark says "matches", not "delivered". The copy beside it is explicit about which,
              because a tick is read as a receipt. */}
          <span className="lq-iroute__mark" aria-hidden="true">
            ✓
          </span>
          <div className="lq-iroute__body">
            <strong className="lq-iroute__name">{rule.destination}</strong>
            <div className="lq-iroute__note">
              {t('incidents.routing.matched', {
                severity: t(`incidents.severity.${rule.min_severity}`),
                kind:
                  rule.incident_kind === null
                    ? t('incidents.routing.anyKind')
                    : t(`incidents.kind.${rule.incident_kind}`),
              })}
            </div>
          </div>
        </div>
      ))}
      {!policy.data.declared && (
        <p className="lq-iroute__none">{t('incidents.routing.isDefault')}</p>
      )}
    </>
  );
}

/* **What actually happened, as opposed to who would be told.**
 *
 * `Routing` above answers "which of this tenant's rules match this incident" and its comment used
 * to state why it could not do more: *"This platform records delivery attempts and exposes them
 * through no route, so a tick here would be an assertion nothing behind this screen can support."*
 * `GET /incidents/{id}/notifications` is that route, so this panel shows the receipts and `Routing`
 * keeps answering the question it was written for. The two are deliberately separate: a rule that
 * matches and a delivery that happened are different facts, and one panel showing both would invite
 * a reader to take the first for the second.
 *
 * **The load-bearing case is the empty one.** No attempts means no delivery was *tried* — most often
 * because the platform default routes critical only and this is a warning — and it never means
 * delivery failed. A panel that read `length === 0` as a fault would report a policy decision as an
 * outage, on the one surface whose purpose is telling those apart. `deliveryReading` makes that a
 * named verdict rather than a length check, and the empty state says which explanation to look at.
 */
/** Whether a ticket was raised for this incident, and where it went.
 *
 * **The sibling of `Delivery`, and the distinction it has to hold is the same one.** An alert tells
 * a person and a ticket opens a record in the system their work is tracked in; both can be
 * *deliberately* absent, and reporting a decision as a gap is the defect both panels exist to
 * avoid. `attempted: false` is the tenant's own routing policy saying this incident raises no
 * ticket — the contract names that field for exactly this reason.
 *
 * **`jobTone` rather than a tone decided here.** It is exported from `features/itsm` precisely so
 * any screen rendering a job state reaches the same verdict about `retired`, which is the one state
 * a careless second implementation paints red: a job whose service desk was removed or switched off
 * while it waited has nothing wrong with it.
 */
function Ticket({ incident }: { readonly incident: Incident }): ReactElement {
  const { t } = useTranslation();
  const ticket = useIncidentTicket(incident.id);

  if (ticket.isPending) return <SkeletonRows rows={1} columns={2} />;
  if (ticket.isError) {
    /* A failure of the *request*, never "no ticket was raised". The same class `Delivery` records
       three instances of: a claim about the data used to report a failure of the read. A 503 is
       ordinary here — a deployment with no ITSM wiring serves no ticket status — and the server's
       own Problem detail says which. */
    return <Alert tone="error">{ticket.error.message}</Alert>;
  }

  const status = ticket.data;

  if (!status.attempted) {
    /* **A decision, not a gap.** No rule of this tenant's matches this incident, which is a
       sentence about their configuration rather than about this incident — so it reads as a
       setting and offers the way to change it rather than reporting an absence. */
    return <p className="lq-iroute__none">{t('incidents.ticket.notRouted')}</p>;
  }

  const reference = status.ticket;

  return (
    <>
      <StatusBadge
        tone={status.state === null ? 'neutral' : jobTone(status.state)}
        label={t(`incidents.ticket.state.${status.state ?? 'pending'}`)}
        title={t(`incidents.ticket.meaning.${status.state ?? 'pending'}`)}
      />

      {reference === null ? null : (
        <div className="lq-iticket">
          <ProviderMark provider={reference.provider} />
          <div className="lq-iticket__body">
            {/* **The key is the link.** `external_url` is the provider's human URL rather than its
                REST one — the connector is explicit about that — so this opens the ticket a person
                reads, not JSON. `noreferrer` because the destination is the customer's own service
                desk and the referrer would name this screen's path. */}
            <a
              className="lq-iticket__key"
              href={reference.external_url}
              target="_blank"
              rel="noreferrer"
            >
              {reference.external_key}
              <span className="lq-iticket__out" aria-hidden="true">
                ↗
              </span>
            </a>
            <span className="lq-iticket__where">
              {t('incidents.ticket.raisedIn', {
                desk: reference.connection_name,
                at: instant(reference.created_at),
              })}
            </span>
          </div>
        </div>
      )}

      {/* The failure classification, and never the provider's own message — the contract says so,
          because a 4xx body from a service desk routinely echoes the request. `credential_unreadable`
          is the one that matters most to separate: it means Lumiq could not read its own stored
          credential, so the thing to look at is this deployment rather than the service desk. */}
      {status.detail === null ? null : (
        <p className="lq-iticket__why">
          {t(`incidents.ticket.why.${status.detail}`, {
            defaultValue: t('incidents.ticket.why.unknown'),
          })}
        </p>
      )}

      {/* Attempts and the next try, together, because either alone misleads: "3 attempts" with no
          next time reads as given up, and a next time with no count hides that this has been
          failing for hours. A terminal job has no `next_attempt_at` by construction — including a
          succeeded one, where a date would read as a pending retry. */}
      {status.attempts === 0 ? null : (
        <p className="lq-iticket__tries">
          {status.next_attempt_at === null
            ? t('incidents.ticket.attempts', { count: status.attempts })
            : t('incidents.ticket.attemptsNext', {
                count: status.attempts,
                at: instant(status.next_attempt_at),
              })}
        </p>
      )}
    </>
  );
}

function Delivery({ incident }: { readonly incident: Incident }): ReactElement {
  const { t } = useTranslation();
  const attempts = useIncidentNotifications(incident.id);

  if (attempts.isPending) return <SkeletonRows rows={2} columns={3} />;
  if (attempts.isError) {
    /* Reported as a failure of the *request*, never as "nobody was told". Third instance of the
       class this codebase records: a claim about the data used to report a failure of the read. A
       503 here is also ordinary — a deployment that records attempts and does not serve the read —
       and the server's own Problem detail says which. */
    return <Alert tone="error">{attempts.error.message}</Alert>;
  }

  const reading = deliveryReading(attempts.data);

  if (reading.verdict === 'nothing_attempted') {
    return (
      <p className="lq-iroute__none">
        {t(
          isDefaultNotifiedSeverity(incident.severity)
            ? 'incidents.delivery.noneCritical'
            : 'incidents.delivery.noneBelowFloor',
          { severity: t(`incidents.severity.${incident.severity}`) },
        )}
      </p>
    );
  }

  return (
    <>
      {/* The summary line first, because "did anybody get this" is the question and a reader should
          not have to count rows to answer it. `partial` exists because a tenant may route one
          incident to a mailbox and a webhook, and a single boolean would report the delivered email
          while the failing webhook went unnoticed. */}
      <StatusBadge
        tone={deliveryTone(reading.verdict)}
        label={t(`incidents.delivery.verdict.${reading.verdict}`, {
          delivered: reading.delivered,
          failed: reading.failed,
        })}
        title={t(`incidents.delivery.meaning.${reading.verdict}`)}
      />
      {attempts.data.map((attempt, index) => (
        <div
          /* Keyed on the instant *and* the channel, because one incident can be routed to two
             destinations in the same pass — so the timestamp alone is not unique. The index is the
             last resort rather than the first: two rows sharing both would otherwise merge. */
          key={`${attempt.attempted_at}:${attempt.channel}:${String(index)}`}
          className="lq-idelivery"
        >
          <span
            className={
              attempt.succeeded
                ? 'lq-idelivery__mark lq-idelivery__mark--ok'
                : 'lq-idelivery__mark lq-idelivery__mark--bad'
            }
            aria-hidden="true"
          >
            {attempt.succeeded ? '✓' : '✕'}
          </span>
          <div className="lq-idelivery__body">
            <strong className="lq-idelivery__channel">
              {/* The transport and the instant. **Not the recipient** — the log stores none, and a
                  destination read out of the routing rules would be this screen guessing which rule
                  produced which attempt. */}
              {t(`incidents.delivery.channel.${attempt.channel}`)}
              <span className="lq-idelivery__when">{instant(attempt.attempted_at)}</span>
            </strong>
            {/* `detail` is the server's own host-written reason and is `""` on success, which the
                type guarantees — a success carrying a reason and a failure carrying none are both
                refused at construction. So this renders only when there is something to say. */}
            {attempt.detail !== '' && (
              <div className="lq-idelivery__why">{attempt.detail}</div>
            )}
          </div>
        </div>
      ))}
    </>
  );
}

export function IncidentDetail(): ReactElement {
  const { t } = useTranslation();
  const { incidentId = '' } = useParams<{ incidentId: string }>();
  const identity = useIdentity();
  const incident = useIncident(incidentId);
  const names = useDatasetNames();
  const acknowledge = useAcknowledgeIncident();
  const resolve = useResolveIncident();
  const now = new Date();

  const back = (
    <div className="lq-icrumb">
      <Link to={SCREEN_PATHS.incidents}>{t('incidents.title')}</Link>{' '}
      <span className="lq-muted">/ {shortIncidentId(incidentId)}</span>
    </div>
  );

  if (incident.isPending) {
    return (
      <>
        {back}
        <p role="status" className="lq-muted">
          {t('incidents.loading')}
        </p>
        <SkeletonRows rows={6} columns={2} />
      </>
    );
  }

  if (incident.isError) {
    return (
      <>
        {back}
        {/* A 404 here is the server saying this incident does not exist *for this tenant*, which is
            deliberately indistinguishable from another tenant's id. The server's own words say so
            better than a guess would. */}
        <EmptyState
          title={t('incidents.detail.missingTitle')}
          guidance={incident.error.message}
        />
      </>
    );
  }

  const it = incident.data;
  const reading = ageReading(it, now);
  const failure = acknowledge.error ?? resolve.error;
  /* Three subject kinds now, and the note below must name the right one: a rule-binding
     incident described as being about a contract is a false statement about a customer's data. */
  const subject = subjectKindOf(it.kind);
  const naming =
    subject === 'dataset' ? nameDataset(it.subject_id, names.data ?? null) : null;
  const ackBlocked = blockedReasonKey(it, 'acknowledge');
  const resolveBlocked = blockedReasonKey(it, 'resolve');

  return (
    <div
      className="lq-idetail"
      style={{ '--inc-colour': SEVERITY_COLOUR[it.severity] } as CSSProperties}
    >
      {back}

      <div className="lq-ihead">
        <div className="lq-ihead__main">
          <div className="lq-ihead__eyebrow">
            <span className="lq-inc__severity">
              {t(`incidents.severity.${it.severity}`)}
            </span>
            <span className="lq-inc__id lq-mono">{shortIncidentId(it.id)}</span>
          </div>
          {/* The **kind** is the heading, because that is what happened. The id is a label above it —
              a reader arriving from an alert is looking for "freshness breach", not a UUID. */}
          <h1 className="lq-ihead__title">{t(`incidents.kind.${it.kind}`)}</h1>
          <p className="lq-ihead__summary">{it.summary}</p>
        </div>
        <span className="lq-istate" title={t(stateMeaningKey(it.state))}>
          <span className="lq-istate__dot" aria-hidden="true" />
          {t(stateLabelKey(it.state))}
        </span>
      </div>

      {/* The state's meaning, in full, on its own band. It is the sentence this whole screen exists
          to make unambiguous — acknowledged is not resolved — and a tooltip is not where it
          belongs. */}
      <div className="lq-imeaning">{t(stateMeaningKey(it.state))}</div>

      <div className="lq-iactions">
        {canAcknowledge(it) && (
          <button
            type="button"
            className="lq-inc__ghost"
            disabled={acknowledge.isPending}
            onClick={() => {
              acknowledge.mutate(it.id);
            }}
          >
            {t('incidents.action.acknowledge')}
          </button>
        )}
        {canResolve(it) && (
          <button
            type="button"
            className="lq-inc__primary"
            disabled={resolve.isPending}
            onClick={() => {
              resolve.mutate(it.id);
            }}
          >
            {t('incidents.action.resolve')}
          </button>
        )}
        {/* A refused action stays on screen as a dead chip carrying its reason, rather than
            disappearing. A reader who expects an Acknowledge control and finds nothing cannot tell a
            missing feature from a state machine; this tells them which. */}
        {ackBlocked !== null && (
          <span className="lq-iblocked" title={t(ackBlocked)}>
            {t('incidents.action.acknowledge')}
          </span>
        )}
        {resolveBlocked !== null && (
          <span className="lq-iblocked" title={t(resolveBlocked)}>
            {t('incidents.action.resolve')}
          </span>
        )}
        {naming !== null && (
          <Link className="lq-iactions__link" to={`/datasets/${it.subject_id}`}>
            {t('incidents.detail.openDataset')}
          </Link>
        )}
        {/* Root-cause tracing is the lineage graph read at the instant the incident opened, not a
            second mechanism: `?asOf=` is the exact contract `LineageScreen` already reads from its
            URL, so a rule failure on a dataset opens straight onto "what fed this table when it
            broke" rather than today's graph, which may have already moved on. Guarded on `subject`
            rather than on `naming`, because lineage resolves the node from `subject_id` alone and
            needs no catalogue name — a dataset the catalogue cannot currently name is still a
            dataset lineage can still be asked about. */}
        {subject === 'dataset' && (
          <Link
            className="lq-iactions__link"
            to={`${SCREEN_PATHS.lineage}?datasetId=${encodeURIComponent(it.subject_id)}&asOf=${encodeURIComponent(it.opened_at)}`}
          >
            {t('incidents.detail.traceRootCause')}
          </Link>
        )}
        <Link className="lq-iactions__link" to={SCREEN_PATHS.alertRouting}>
          {t('incidents.detail.checkRouting')}
        </Link>
        {/* Ticketing beside alert routing, because they are the two halves of one question: an
            alert tells a person and a ticket opens a record in the system their work is tracked
            in. A reader standing on an incident asking "was anybody told" wants both, and having
            one link here and not the other would read as the platform having only one answer.

            Admin only, since `/admin/integrations` is behind `RequireAdmin` — the link would
            otherwise be a control that lands on a refusal. */}
        {isAdmin(identity.data) && (
          <Link className="lq-iactions__link" to={SCREEN_PATHS.integrations}>
            {t('incidents.detail.configureTicketing')}
          </Link>
        )}
      </div>

      {failure !== null && (
        <div className="lq-iactions__error">
          <Alert tone="error">{failure.message}</Alert>
        </div>
      )}

      <div className="lq-ipanels">
        <Panel
          heading={t('incidents.detail.incidentPanel')}
          /* One key shape for all three kinds. The dataset case used to have its own
             differently-named key, so adding the third left three notes under two conventions —
             and a `subjectNote.dataset` an author would reasonably reach for resolved to nothing. */
          note={t(`incidents.detail.subjectNote.${subject}`)}
        >
          <Fact label={t('incidents.fact.opened')}>{instant(it.opened_at)}</Fact>
          <Fact label={t('incidents.fact.held')}>
            {reading === null ? (
              <span className="lq-muted">—</span>
            ) : (
              t(reading.key, { count: reading.count })
            )}
          </Fact>
          {/* `cause` is the machine-readable reason code a notification template is keyed on and a
              dashboard groups by. Kept visible and in mono so an operator comparing this page
              against an alert sees the same string. */}
          <Fact label={t('incidents.fact.reason')} mono>
            {it.cause}
          </Fact>
          <Fact label={t('incidents.fact.subject')} mono>
            {naming === null ? (
              /* A contract breach's subject is a contract, so no catalogue name is resolved — and
                 the id is shown rather than a lookup that would report a missing dataset. */
              <span className="lq-muted">{t('incidents.subjectNotADataset')}</span>
            ) : naming.kind === 'named' ? (
              <>
                <DatasetKindIcon datasetId={it.subject_id} />
                <Link to={`/datasets/${it.subject_id}`}>{naming.name}</Link>
              </>
            ) : (
              <span className="lq-muted">{t(naming.reasonKey)}</span>
            )}
          </Fact>
          <Fact label={t('incidents.fact.severity')}>
            {t(`incidents.severity.${it.severity}`)}
          </Fact>
          <Fact label={t('incidents.fact.state')}>{t(stateLabelKey(it.state))}</Fact>
          <Fact label={t('incidents.fact.evidence')} mono>
            {it.evidence_ref ?? (
              /* Absence is not zero: no evidence reference means the monitor recorded none, which is
                 different from an observation that measured nothing. */
              <span className="lq-muted">{t('incidents.fact.noEvidence')}</span>
            )}
          </Fact>
        </Panel>

        <Panel
          heading={t('incidents.detail.routingPanel')}
          note={t('incidents.detail.routingNote')}
        >
          <Routing incident={it} />
        </Panel>
      </div>

      <Panel
        heading={t('incidents.detail.deliveryPanel')}
        note={t('incidents.detail.deliveryNote')}
      >
        <Delivery incident={it} />
      </Panel>

      {/* After Delivery, because the two answer the same question of different systems and a
          reader asking "was anybody told" wants both. Its own panel rather than a row inside
          Delivery: an alert attempt and a ticket job have different lifecycles — an alert is tried
          once and a ticket is queued, retried and can be stood down. */}
      <Panel
        heading={t('incidents.detail.ticketPanel')}
        note={t('incidents.detail.ticketNote')}
      >
        <Ticket incident={it} />
      </Panel>

      <Panel
        heading={t('incidents.detail.timelinePanel')}
        note={t('incidents.detail.timelineNote')}
      >
        {timelineFor(it).map((entry) => (
          <div key={entry.verb} className="lq-itl">
            <div className="lq-itl__when">
              {entry.at === null ? (
                /* **There is no `acknowledged_at`** — not on the wire and not in the table. A
                   borrowed timestamp would read as the moment somebody picked it up, so the column
                   stays honestly blank and says why. */
                <span className="lq-muted">{t('incidents.timeline.noTime')}</span>
              ) : (
                instant(entry.at)
              )}
            </div>
            <div className={`lq-itl__verb lq-itl__verb--${entry.verb}`}>
              {t(`incidents.timeline.${entry.verb}`)}
            </div>
            <div className="lq-itl__what">
              {t(`incidents.timeline.${entry.verb}.what`)}
            </div>
            <div className="lq-itl__who">
              {entry.who ?? (
                <span className="lq-muted">{t('incidents.by.monitorShort')}</span>
              )}
            </div>
          </div>
        ))}
      </Panel>
    </div>
  );
}
